import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["captureArea", "icon"];
  static values = {
    rendererUrl: String,
    scale: { type: Number, default: 1 },
    maxPixels: { type: Number, default: 1200000 },
    minScale: { type: Number, default: 0.8 },
    timeoutMs: { type: Number, default: 10000 },
  };

  async copy() {
    if (this.copying) return;

    // The fixed roster sits beside the header/jobs capture area. Capture the
    // column so exporting a slot includes both independently scrolling lists.
    const element = this.element;
    const captureBox = this.captureBoxFor(element);
    const scale = this.captureScaleFor(captureBox);

    try {
      this.copying = true;

      const isDark = document.documentElement.classList.contains("dark");
      const backgroundColor = isDark ? "#334155" : "#ffffff"; // slate-700 / white

      // Safari requires ClipboardItem to be created synchronously within
      // the user gesture, with the blob passed as a Promise (not awaited).
      const blobPromise = this.captureAsBlob(element, {
        captureBox,
        scale,
        backgroundColor,
      });

      await this.writeImageToClipboard(blobPromise);
      this.showSuccess();
    } catch (error) {
      console.error("Failed to copy slot as image:", error);
      this.showError();
    } finally {
      this.copying = false;
    }
  }

  async writeImageToClipboard(blobPromise) {
    if (this.requiresSynchronousClipboardItem()) {
      return this.withTimeout(
        navigator.clipboard.write([
          new ClipboardItem({
            "image/png": blobPromise,
          }),
        ]),
        "Timed out writing slot image to the clipboard.",
      );
    }

    const blob = await this.withTimeout(
      blobPromise,
      "Timed out rendering slot image.",
    );

    return this.withTimeout(
      navigator.clipboard.write([
        new ClipboardItem({
          "image/png": blob,
        }),
      ]),
      "Timed out writing slot image to the clipboard.",
    );
  }

  requiresSynchronousClipboardItem() {
    const userAgent = navigator.userAgent || "";
    return /Safari/.test(userAgent) && !/Chrome|Chromium|Edg\//.test(userAgent);
  }

  withTimeout(promise, message) {
    let timeoutId;

    const timeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(message)), this.timeoutMsValue);
    });

    return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId));
  }

  async captureAsBlob(element, { captureBox, scale, backgroundColor }) {
    const { domToCanvas } = await this.loadRenderer();
    const canvas = await domToCanvas(element, {
      scale,
      width: captureBox.width,
      height: captureBox.height,
      backgroundColor,
      includeStyleProperties: this.captureStyleProperties(),
      style: {
        flex: "0 0 auto",
        minHeight: "0px",
      },
      filter: (node) => {
        if (this.shouldExcludeFromCapture(node)) {
          return false;
        }
        return true;
      },
      onCloneNode: (node) => this.prepareClonedTreeForCapture(node),
    });

    // Trim empty background rows, then add padding + shadow
    // so the screenshot looks like an elevated card.
    const trimmed = this.trimBottom(canvas, backgroundColor);
    const final = this.addShadowFrame(trimmed, backgroundColor, scale);
    return new Promise((resolve, reject) => {
      final.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create slot image blob."));
        }
      }, "image/png");
    });
  }

  async loadRenderer() {
    if (window.CopySlotImageRenderer) return window.CopySlotImageRenderer;
    if (!this.hasRendererUrlValue) throw new Error("Copy image renderer URL is unavailable.");

    window.copySlotImageRendererPromise ||= new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = this.rendererUrlValue;
      script.dataset.copySlotImageRenderer = "true";
      script.addEventListener("load", () => {
        if (window.CopySlotImageRenderer) {
          resolve(window.CopySlotImageRenderer);
        } else {
          reject(new Error("Copy image renderer did not initialize."));
        }
      }, { once: true });
      script.addEventListener("error", () => reject(new Error("Copy image renderer could not be loaded.")), { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      window.copySlotImageRendererPromise = null;
      throw error;
    });

    return window.copySlotImageRendererPromise;
  }

  captureBoxFor(element) {
    const rect = element.getBoundingClientRect();

    return {
      width: Math.max(1, Math.ceil(rect.width)),
      height: Math.max(1, Math.ceil(this.contentHeightFor(element, rect))) + this.reflowAllowanceFor(element),
    };
  }

  contentHeightFor(element, elementRect) {
    const jobsViewport = element.querySelector("[data-slot-jobs-viewport]");
    if (jobsViewport) {
      // Use the list's intrinsic height, not its stretched viewport: a short
      // job list should not leave a screenful of whitespace above the roster.
      const jobsContent = jobsViewport.firstElementChild;
      let height = jobsViewport.getBoundingClientRect().top - elementRect.top +
        (jobsContent?.scrollHeight || jobsViewport.scrollHeight);
      const roster = element.querySelector(".canvas-slot-roster");
      const rosterViewport = roster?.querySelector(".canvas-slot-roster-viewport");
      if (rosterViewport && !this.isRosterWithoutPeople(roster)) {
        height += (parseFloat(window.getComputedStyle(roster).marginTop) || 0) +
          rosterViewport.getBoundingClientRect().top - roster.getBoundingClientRect().top +
          rosterViewport.scrollHeight;
      }
      return height + (parseFloat(window.getComputedStyle(element).paddingBottom) || 0) + 2;
    }

    let bottom = 0;

    element.querySelectorAll("*").forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (this.shouldExcludeFromCapture(node)) return;

      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden") return;

      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const marginBottom = parseFloat(style.marginBottom) || 0;
      bottom = Math.max(bottom, rect.bottom - elementRect.top + marginBottom);
    });

    if (bottom > 0) return bottom + 2;

    return element.scrollHeight || elementRect.height || 1;
  }

  // The clone can render text wider than the live canvas (the web font is not
  // embedded), so a title may need more lines there and a person may move to
  // a row of their own. Reserve that growth for everything the clone
  // stylesheet lets resize: one line per title plus a quarter of its height,
  // since a title that already spans many lines can gain several. trimBottom
  // drops whatever stays unused.
  reflowAllowanceFor(element) {
    let allowance = 0;

    element.querySelectorAll("[data-assigned-job-name], [data-slot-title-row] h2").forEach((title) => {
      const style = window.getComputedStyle(title);
      allowance += (parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 16) * 1.5) +
        title.getBoundingClientRect().height * 0.25;
    });

    element.querySelectorAll("[data-assigned-job-card-body] [data-assigned-person-id]").forEach((person) => {
      const style = window.getComputedStyle(person);
      allowance += person.getBoundingClientRect().height +
        (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
    });

    return Math.ceil(allowance);
  }

  captureScaleFor({ width, height }) {
    const requestedScale = this.positiveNumber(this.scaleValue, 1);
    const minScale = this.positiveNumber(this.minScaleValue, 0.8);
    const maxPixels = this.positiveNumber(this.maxPixelsValue, 1200000);
    const currentPixels = Math.max(1, width * height);
    const cappedScale = Math.sqrt(maxPixels / currentPixels);
    const scale = Math.min(requestedScale, cappedScale);
    const usableScale = cappedScale >= minScale ? Math.max(minScale, scale) : scale;

    return this.roundScaleDown(usableScale);
  }

  positiveNumber(value, fallback) {
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  roundScaleDown(scale) {
    const rounded = Math.floor(scale * 100) / 100;
    return rounded > 0 ? rounded : scale;
  }

  shouldExcludeFromCapture(node) {
    if (!(node instanceof HTMLElement)) return false;

    return node.dataset.captureExclude !== undefined ||
      node.closest("[data-capture-exclude], [data-slot-edit-bar], [data-assigned-job-edit-bar]") !== null ||
      this.isRosterWithoutPeople(node);
  }

  // A roster that holds only its add control would export as a bare divider.
  isRosterWithoutPeople(node) {
    return node.classList.contains("canvas-slot-roster") &&
      node.querySelector("[data-slot-people-staging-person]") === null;
  }

  captureStyleProperties() {
    return [
      "align-content",
      "align-items",
      "align-self",
      "aspect-ratio",
      "background",
      "background-clip",
      "background-color",
      "background-image",
      "background-position",
      "background-size",
      "border",
      "border-bottom",
      "border-bottom-color",
      "border-bottom-left-radius",
      "border-bottom-right-radius",
      "border-bottom-style",
      "border-bottom-width",
      "border-collapse",
      "border-color",
      "border-left",
      "border-left-color",
      "border-left-style",
      "border-left-width",
      "border-radius",
      "border-right",
      "border-right-color",
      "border-right-style",
      "border-right-width",
      "border-spacing",
      "border-style",
      "border-top",
      "border-top-color",
      "border-top-left-radius",
      "border-top-right-radius",
      "border-top-style",
      "border-top-width",
      "border-width",
      "bottom",
      "box-shadow",
      "box-sizing",
      "color",
      "column-gap",
      "content",
      "display",
      "flex",
      "flex-basis",
      "flex-direction",
      "flex-grow",
      "flex-shrink",
      "flex-wrap",
      "font",
      "font-family",
      "font-size",
      "font-style",
      "font-variant",
      "font-weight",
      "gap",
      "grid-column",
      "grid-row",
      "grid-template-columns",
      "height",
      "inset",
      "justify-content",
      "left",
      "letter-spacing",
      "line-height",
      "margin",
      "margin-bottom",
      "margin-left",
      "margin-right",
      "margin-top",
      "max-height",
      "max-width",
      "min-height",
      "min-width",
      "object-fit",
      "opacity",
      "order",
      "overflow",
      "overflow-wrap",
      "padding",
      "padding-bottom",
      "padding-left",
      "padding-right",
      "padding-top",
      "position",
      "right",
      "row-gap",
      "table-layout",
      "text-align",
      "text-decoration",
      "text-overflow",
      "text-transform",
      "top",
      "transform",
      "vertical-align",
      "visibility",
      "white-space",
      "width",
      "word-break",
      "z-index",
    ];
  }

  prepareClonedTreeForCapture(node) {
    if (!(node instanceof HTMLElement)) return;

    const style = node.ownerDocument.createElement("style");
    style.textContent = `
      [data-copy-slot-image-target~="captureArea"],
      .canvas-slot-jobs-scroll,
      [data-slot-jobs-viewport],
      .canvas-slot-roster,
      .canvas-slot-roster-scroll,
      .canvas-slot-roster-viewport {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        flex: none !important;
      }

      [data-slot-jobs-viewport] > *,
      .canvas-slot-roster-viewport > * {
        transform: none !important;
      }

      /* Titles, names and the boxes that contain them size to their content.
         Their live pixel sizes are copied inline, and text that wraps
         differently in the clone would otherwise spill over the row below. */
      [id^="canvas_slot_header_"],
      [data-slot-title-row],
      [data-slot-title-row] > div,
      [data-slot-title-row] > div > div,
      [data-slot-title-row] h2,
      [data-slot-title-row] h2 > [data-inline-edit-target~="display"],
      [data-slot-jobs-viewport] > *,
      [data-slot-assigned-jobs-list],
      [data-slot-assigned-job-card],
      [data-assigned-job-card-body],
      [data-assigned-job-header],
      [data-assigned-job-header] > .inline-tail,
      [data-assigned-job-header] > .inline-tail > span,
      [data-assigned-job-name],
      [data-assigned-job-card-body] [data-inline-tail-measure-container],
      [data-assigned-job-card-body] [data-assigned-person-id],
      [data-assigned-job-card-body] [data-assigned-job-person-problem],
      [data-assigned-job-card-body] [data-assigned-job-person-problem] > .inline-tail,
      [data-assigned-job-card-body] [data-assigned-job-person-name] {
        height: auto !important;
      }

      /* Controls left out of the capture must not keep the room they had. */
      .canvas-slot-roster-viewport > *,
      [data-slot-people-staging-person] {
        height: auto !important;
      }

      [data-slot-people-staging-person] {
        grid-template-columns: minmax(0, 1fr) !important;
        width: auto !important;
      }

      [data-slot-title-row] h2,
      [data-slot-title-row] h2 > [data-inline-edit-target~="display"],
      [data-assigned-job-header] > .inline-tail > span,
      [data-assigned-job-name],
      [data-assigned-job-card-body] [data-assigned-person-id],
      [data-assigned-job-card-body] [data-assigned-job-person-problem],
      [data-assigned-job-card-body] [data-assigned-job-person-problem] > .inline-tail,
      [data-assigned-job-card-body] [data-assigned-job-person-name] {
        width: auto !important;
      }

      .canvas-scroll-region::before,
      .canvas-scroll-region::after {
        display: none !important;
      }

      [data-assigned-job-person-name],
      [data-assigned-job-person-name] * {
        overflow-wrap: normal !important;
        word-break: normal !important;
      }

      [data-slot-staffing-summary],
      [data-slot-staffing-summary] * {
        overflow-wrap: normal !important;
        white-space: nowrap !important;
        word-break: normal !important;
      }

      [data-assigned-job-staffing-requirement],
      [data-assigned-job-staffing-requirement] * {
        overflow-wrap: normal !important;
        word-break: normal !important;
        white-space: nowrap !important;
      }
    `;
    node.prepend(style);
  }

  trimBottom(canvas, hexColor) {
    const ctx = canvas.getContext("2d");
    const [bgR, bgG, bgB] = this.parseHex(hexColor);
    const { width, height } = canvas;
    const tolerance = 10;

    const chunkHeight = 32;
    const pixelStride = 4;

    for (let chunkBottom = height; chunkBottom > 0; chunkBottom -= chunkHeight) {
      const chunkTop = Math.max(0, chunkBottom - chunkHeight);
      const chunk = ctx.getImageData(0, chunkTop, width, chunkBottom - chunkTop).data;

      for (let y = chunkBottom - chunkTop - 1; y >= 0; y--) {
        const rowOffset = y * width * 4;
        for (let x = 0; x < width; x += pixelStride) {
          const i = rowOffset + x * 4;
          if (
            Math.abs(chunk[i] - bgR) > tolerance ||
            Math.abs(chunk[i + 1] - bgG) > tolerance ||
            Math.abs(chunk[i + 2] - bgB) > tolerance
          ) {
            const cropHeight = chunkTop + y + 1;
            return cropHeight < height ? this.cropCanvas(canvas, cropHeight) : canvas;
          }
        }
      }
    }

    return canvas;
  }

  addShadowFrame(source, backgroundColor, scale) {
    const frameScale = Math.max(scale, 1);
    const outerPad = Math.round(20 * frameScale);
    const innerPad = Math.round(18 * frameScale);
    const shadowBlur = Math.round(8 * frameScale);
    const shadowOffsetY = Math.round(2 * frameScale);
    const borderRadius = Math.round(8 * frameScale);

    const cardW = source.width + innerPad * 2;
    const cardH = source.height + innerPad * 2;

    const out = document.createElement("canvas");
    out.width = cardW + outerPad * 2;
    out.height = cardH + outerPad * 2;
    const ctx = out.getContext("2d");

    // Fill outer background with the app's blue tint
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.fillStyle = "rgba(59, 130, 246, 0.5)";
    ctx.fillRect(0, 0, out.width, out.height);

    // Draw drop shadow from the card rect
    ctx.shadowColor = "rgba(0,0,0,0.18)";
    ctx.shadowBlur = shadowBlur;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = shadowOffsetY;

    ctx.beginPath();
    ctx.roundRect(outerPad, outerPad, cardW, cardH, borderRadius);
    ctx.fillStyle = backgroundColor;
    ctx.fill();

    // Reset shadow before drawing content
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Clip to rounded rect and draw content with inner padding
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(outerPad, outerPad, cardW, cardH, borderRadius);
    ctx.clip();
    ctx.drawImage(source, outerPad + innerPad, outerPad + innerPad);
    ctx.restore();

    return out;
  }

  cropCanvas(source, cropHeight) {
    const cropped = document.createElement("canvas");
    cropped.width = source.width;
    cropped.height = cropHeight;
    const ctx = cropped.getContext("2d");
    ctx.drawImage(source, 0, 0, source.width, cropHeight, 0, 0, source.width, cropHeight);
    return cropped;
  }

  parseHex(hex) {
    const c = hex.replace("#", "");
    return [
      parseInt(c.substring(0, 2), 16),
      parseInt(c.substring(2, 4), 16),
      parseInt(c.substring(4, 6), 16),
    ];
  }

  showSuccess() {
    this.swapIcon("ti-check", "text-green-500");
  }

  showError() {
    this.swapIcon("ti-close", "text-red-500");
  }

  swapIcon(iconClass, colorClass) {
    if (!this.hasIconTarget) return;

    const icon = this.iconTarget;
    const button = icon.closest("button");
    this._originalClasses = this._originalClasses || [...icon.classList];

    icon.className = "";
    icon.classList.add("ti", iconClass, colorClass);

    // Reset icon once the mouse leaves the button
    const resetOnLeave = () => {
      icon.className = "";
      this._originalClasses.forEach((cls) => icon.classList.add(cls));
      this._originalClasses = null;
    };

    if (button) {
      button.removeEventListener("mouseleave", this._resetHandler);
      this._resetHandler = resetOnLeave;
      button.addEventListener("mouseleave", resetOnLeave, { once: true });
    }
  }
}
